import React from 'react'
import { useDemoMode } from '@/contexts/DemoModeContext'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Beaker, AlertTriangle } from 'lucide-react'

interface DemoModeToggleProps {
  showLabel?: boolean
  variant?: 'switch' | 'badge' | 'button'
}

export function DemoModeToggle({ showLabel = true, variant = 'switch' }: DemoModeToggleProps) {
  const { isDemoMode, toggleDemoMode } = useDemoMode()

  if (variant === 'badge') {
    return isDemoMode ? (
      <Badge
        variant="outline"
        className="cursor-pointer bg-amber-500/10 text-amber-500 border-amber-500/20 hover:bg-amber-500/20"
        onClick={toggleDemoMode}
      >
        <Beaker className="w-3 h-3 mr-1" />
        Demo Mode
      </Badge>
    ) : null
  }

  if (variant === 'button') {
    return (
      <button
        onClick={toggleDemoMode}
        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
          isDemoMode
            ? 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
            : 'bg-muted text-muted-foreground hover:bg-muted/80'
        }`}
      >
        <Beaker className="w-4 h-4" />
        {isDemoMode ? 'Exit Demo' : 'Demo Mode'}
      </button>
    )
  }

  return (
    <div className="flex items-center justify-between gap-4">
      {showLabel && (
        <div className="flex items-center gap-2">
          <Beaker className={`w-4 h-4 ${isDemoMode ? 'text-amber-500' : 'text-muted-foreground'}`} />
          <span className="text-sm font-medium">Demo Mode</span>
        </div>
      )}
      <Switch
        checked={isDemoMode}
        onCheckedChange={toggleDemoMode}
        className={isDemoMode ? 'data-[state=checked]:bg-amber-500' : ''}
      />
    </div>
  )
}

export function DemoModeBanner() {
  const { isDemoMode, disableDemoMode } = useDemoMode()

  if (!isDemoMode) return null

  return (
    <div className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2">
      <div className="flex items-center justify-between max-w-screen-2xl mx-auto">
        <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <AlertTriangle className="w-4 h-4" />
          <span className="text-sm font-medium">
            Demo Mode Active - Displaying sample data for demonstration purposes
          </span>
        </div>
        <button
          onClick={disableDemoMode}
          className="text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 underline"
        >
          Exit Demo
        </button>
      </div>
    </div>
  )
}

export default DemoModeToggle
